import React, { useState, useEffect } from 'react';
import AdminSidebar from '../../components/AdminSidebar';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { clientService, contactService } from '../../services/api';
import { useToast } from '../../hooks/use-toast';
import { Plus, Edit, Trash2, Search, Briefcase, Euro, CreditCard, CheckCircle, XCircle, AlertCircle, Upload, FileText, Eye, EyeOff, Download, UserCircle } from 'lucide-react';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

const EMPLOYMENT_STATUS_OPTIONS = [
  { value: 'salarie', label: 'Salarié(e)' },
  { value: 'retraite', label: 'Retraité(e)' },
  { value: 'independant', label: 'Indépendant / Auto-entrepreneur' },
  { value: 'chomeur', label: 'Demandeur d\'emploi' },
  { value: 'etudiant', label: 'Étudiant(e)' },
  { value: 'autre', label: 'Autre' }
];

const LOAN_STATUS_OPTIONS = [
  { value: 'pending', label: 'En attente', color: 'bg-yellow-100 text-yellow-700', icon: AlertCircle },
  { value: 'active', label: 'Actif', color: 'bg-green-100 text-green-700', icon: CheckCircle },
  { value: 'blocked', label: 'Bloqué', color: 'bg-red-100 text-red-700', icon: XCircle }
];

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CONTRACT_TYPES = [
  { value: 'justificatif_revenus', label: 'Justificatif de revenus' },
  { value: 'justificatif_domicile', label: 'Justificatif de domicile' },
  { value: 'justificatif_identite', label: "Justificatif d'identité" },
  { value: 'rib', label: 'RIB' },
  { value: 'contrat_pret', label: 'Contrat de prêt' },
  { value: 'contrat_rachat', label: 'Contrat de rachat de crédit' },
  { value: 'echeancier', label: 'Échéancier' },
  { value: 'tableau_amortissement', label: "Tableau d'amortissement" },
  { value: 'avenant', label: 'Avenant' },
  { value: 'attestation_assurance', label: "Attestation d'assurance" },
  { value: 'releve_compte', label: 'Relevé de compte' },
  { value: 'autre', label: 'Autre document' }
];

const JUSTIFICATIF_TYPES = [
  { value: 'justificatif_identite', label: "Justificatif d'identité" },
  { value: 'releve_identite_bancaire', label: "Relevé d'identité bancaire (RIB)" },
  { value: 'releve_compte', label: 'Relevé de compte' },
  { value: 'avis_imposition', label: "Avis d'imposition" },
  { value: 'justificatif_domicile', label: "Justificatif de domicile" },
  { value: 'bulletin_salaire', label: "Bulletin de salaire" }
];

const ClientsManagement = () => {
  const { toast } = useToast();
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isContractDialogOpen, setIsContractDialogOpen] = useState(false);
  const [isJustificatifDialogOpen, setIsJustificatifDialogOpen] = useState(false);
  const [isDocumentsDialogOpen, setIsDocumentsDialogOpen] = useState(false);
  const [selectedClientForContract, setSelectedClientForContract] = useState(null);
  const [selectedClientForJustificatif, setSelectedClientForJustificatif] = useState(null);
  const [selectedClientForDocuments, setSelectedClientForDocuments] = useState(null);
  const [clientContracts, setClientContracts] = useState([]);
  const [clientJustificatifs, setClientJustificatifs] = useState([]);
  const [contractFile, setContractFile] = useState(null);
  const [contractType, setContractType] = useState('');
  const [contractDescription, setContractDescription] = useState('');
  const [uploadingContract, setUploadingContract] = useState(false);
  const [justificatifFile, setJustificatifFile] = useState(null);
  const [justificatifType, setJustificatifType] = useState('');
  const [uploadingJustificatif, setUploadingJustificatif] = useState(false);
  const [visiblePasswords, setVisiblePasswords] = useState({});
  const [editingClient, setEditingClient] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdvisorDialogOpen, setIsAdvisorDialogOpen] = useState(false);
  const [advisorClient, setAdvisorClient] = useState(null);
  const [advisorForm, setAdvisorForm] = useState({ firstName: '', lastName: '', phone: '', phoneFix: '', email: '' });
  const [savingAdvisor, setSavingAdvisor] = useState(false);
  const [addressSuggestions, setAddressSuggestions] = useState([]);
  const [showAddressSuggestions, setShowAddressSuggestions] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);
  const [newClient, setNewClient] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    password: '',
    balance: 0,
    loanAmount: 20000,
    accountNumber: '',
    employmentStatus: '',
    employer: '',
    monthlyIncome: '',
    employmentStartDate: ''
  });

  useEffect(() => {
    loadClients();
  }, []);

  // Function to search addresses using French government API
  const searchAddress = async (query) => {
    if (!query || query.length < 3) {
      setAddressSuggestions([]);
      setShowAddressSuggestions(false);
      return;
    }

    setAddressLoading(true);
    try {
      const response = await fetch(
        `https://api-adresse.data.gouv.fr/search/?q=${encodeURIComponent(query)}&limit=5`
      );
      const data = await response.json();
      
      if (data.features && data.features.length > 0) {
        const suggestions = data.features.map((feature) => ({
          label: feature.properties.label,
          street: feature.properties.name,
          city: feature.properties.city,
          zipCode: feature.properties.postcode,
          context: feature.properties.context
        }));
        setAddressSuggestions(suggestions);
        setShowAddressSuggestions(true);
      } else {
        setAddressSuggestions([]);
        setShowAddressSuggestions(false);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setAddressSuggestions([]);
    } finally {
      setAddressLoading(false);
    }
  };

  // Handle address input change with debounce
  const handleAddressChange = (e) => {
    const value = e.target.value;
    setNewClient({ ...newClient, address: value });
    
    // Debounce the API call
    clearTimeout(window.addressSearchTimeout);
    window.addressSearchTimeout = setTimeout(() => {
      searchAddress(value);
    }, 300);
  };

  // Handle address suggestion selection
  const selectAddressSuggestion = (suggestion) => {
    setNewClient({
      ...newClient,
      address: suggestion.street,
      city: suggestion.city,
      zipCode: suggestion.zipCode
    });
    setShowAddressSuggestions(false);
    setAddressSuggestions([]);
  };

  const loadClients = async () => {
    try {
      const data = await clientService.getAll();
      setClients(data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de charger les clients',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const filteredClients = clients.filter(
    (client) =>
      client.firstName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.lastName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      client.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleAddClient = async () => {
    try {
      const clientData = {
        ...newClient,
        monthlyIncome: newClient.monthlyIncome ? parseFloat(newClient.monthlyIncome) : null,
        loanAmount: newClient.loanAmount ? parseFloat(newClient.loanAmount) : 20000,
        accountNumber: newClient.accountNumber || null
      };
      const client = await clientService.create(clientData);
      
      setClients([...clients, client]);
      toast({
        title: 'Client créé',
        description: `Le compte de ${client.firstName} ${client.lastName} a été créé. Mot de passe: ${newClient.password}`,
      });
      setIsAddDialogOpen(false);
      resetNewClient();
    } catch (error) {
      console.error('Error creating client:', error);
      toast({
        title: 'Erreur',
        description: error.response?.data?.detail || 'Impossible de créer le client',
        variant: 'destructive',
      });
    }
  };

  const handleEditClient = async () => {
    try {
      const updateData = {
        firstName: editingClient.firstName,
        lastName: editingClient.lastName,
        phone: editingClient.phone,
        address: editingClient.address,
        city: editingClient.city,
        zipCode: editingClient.zipCode,
        loanAmount: editingClient.loanAmount ? parseFloat(editingClient.loanAmount) : null,
        monthlyPayment: editingClient.monthlyPayment ? parseFloat(editingClient.monthlyPayment) : null,
        interestRate: editingClient.interestRate ? parseFloat(editingClient.interestRate) : null,
        numberOfPayments: editingClient.numberOfPayments ? parseInt(editingClient.numberOfPayments) : null,
        loanStatus: editingClient.loanStatus,
        accountNumber: editingClient.accountNumber,
        employmentStatus: editingClient.employmentStatus,
        employer: editingClient.employer,
        monthlyIncome: editingClient.monthlyIncome ? parseFloat(editingClient.monthlyIncome) : null,
        employmentStartDate: editingClient.employmentStartDate
      };
      
      const updatedClient = await clientService.update(editingClient.id, updateData);
      setClients(clients.map(c => c.id === editingClient.id ? updatedClient : c));
      
      toast({
        title: 'Client mis à jour',
        description: `Les informations de ${updatedClient.firstName} ${updatedClient.lastName} ont été mises à jour.`,
      });
      setIsEditDialogOpen(false);
      setEditingClient(null);
    } catch (error) {
      console.error('Error updating client:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de mettre à jour le client',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteClient = async (id) => {
    try {
      await clientService.delete(id);
      setClients(clients.filter((c) => c.id !== id));
      toast({
        title: 'Client supprimé',
        description: 'Le compte client a été supprimé avec succès.',
        variant: 'destructive',
      });
    } catch (error) {
      console.error('Error deleting client:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le client',
        variant: 'destructive',
      });
    }
  };

  const openContractDialog = (client) => {
    setSelectedClientForContract(client);
    setContractFile(null);
    setContractType('');
    setContractDescription('');
    setIsContractDialogOpen(true);
  };

  const openJustificatifDialog = (client) => {
    setSelectedClientForJustificatif(client);
    setJustificatifFile(null);
    setJustificatifType('');
    setIsJustificatifDialogOpen(true);
  };

  // Open Documents Dialog to view/delete contracts and justificatifs
  const openDocumentsDialog = async (client) => {
    setSelectedClientForDocuments(client);
    setIsDocumentsDialogOpen(true);
    
    try {
      // Load contracts
      const contractsRes = await fetch(`${API_URL}/api/admin-contracts/client/${client.id}`);
      if (contractsRes.ok) {
        const contracts = await contractsRes.json();
        setClientContracts(contracts);
      }
      
      // Load justificatifs
      const justificatifsRes = await fetch(`${API_URL}/api/justificatifs/client/${client.id}`);
      if (justificatifsRes.ok) {
        const justificatifs = await justificatifsRes.json();
        setClientJustificatifs(justificatifs);
      }
    } catch (error) {
      console.error('Error loading documents:', error);
    }
  };

  // Delete contract
  const handleDeleteContract = async (contractId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce contrat ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/admin-contracts/${contractId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setClientContracts(prev => prev.filter(c => c.id !== contractId));
        toast({
          title: 'Contrat supprimé',
          description: 'Le contrat a été supprimé avec succès',
        });
      }
    } catch (error) {
      console.error('Error deleting contract:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le contrat',
        variant: 'destructive',
      });
    }
  };

  // Delete justificatif
  const handleDeleteJustificatif = async (justificatifId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce justificatif ?')) return;
    
    try {
      const response = await fetch(`${API_URL}/api/justificatifs/${justificatifId}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        setClientJustificatifs(prev => prev.filter(j => j.id !== justificatifId));
        toast({
          title: 'Justificatif supprimé',
          description: 'Le justificatif a été supprimé avec succès',
        });
      }
    } catch (error) {
      console.error('Error deleting justificatif:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible de supprimer le justificatif',
        variant: 'destructive',
      });
    }
  };

  const handleUploadJustificatif = async () => {
    if (!justificatifType) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un type de justificatif',
        variant: 'destructive',
      });
      return;
    }

    setUploadingJustificatif(true);
    
    try {
      // If file is provided, upload it first
      if (justificatifFile) {
        const formData = new FormData();
        formData.append('file', justificatifFile);
        formData.append('clientId', selectedClientForJustificatif.id);
        formData.append('type', justificatifType);

        await fetch(`${API_URL}/api/justificatifs/upload`, {
          method: 'POST',
          body: formData,
        });
      }

      // Mark as validated
      await fetch(`${API_URL}/api/justificatifs/create-validated`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientId: selectedClientForJustificatif.id,
          type: justificatifType
        }),
      });

      toast({
        title: 'Justificatif ajouté',
        description: `Le justificatif a été validé pour ${selectedClientForJustificatif.firstName} ${selectedClientForJustificatif.lastName}`,
      });
      setIsJustificatifDialogOpen(false);
    } catch (error) {
      console.error('Error uploading justificatif:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter le justificatif',
        variant: 'destructive',
      });
    } finally {
      setUploadingJustificatif(false);
    }
  };

  const handleUploadContract = async () => {
    if (!contractFile || !contractType) {
      toast({
        title: 'Erreur',
        description: 'Veuillez sélectionner un fichier et un type de contrat',
        variant: 'destructive',
      });
      return;
    }

    setUploadingContract(true);
    
    try {
      const formData = new FormData();
      formData.append('file', contractFile);
      formData.append('clientId', selectedClientForContract.id);
      formData.append('contractType', contractType);
      formData.append('description', contractDescription);

      const response = await fetch(`${API_URL}/api/admin-contracts/upload`, {
        method: 'POST',
        body: formData,
      });

      if (response.ok) {
        toast({
          title: 'Contrat ajouté',
          description: `Le contrat a été ajouté pour ${selectedClientForContract.firstName} ${selectedClientForContract.lastName}`,
        });
        setIsContractDialogOpen(false);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading contract:', error);
      toast({
        title: 'Erreur',
        description: 'Impossible d\'ajouter le contrat',
        variant: 'destructive',
      });
    } finally {
      setUploadingContract(false);
    }
  };

  const resetNewClient = () => {
    setNewClient({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      zipCode: '',
      password: '',
      balance: 0,
      loanAmount: 20000,
      accountNumber: '',
      employmentStatus: '',
      employer: '',
      monthlyIncome: '',
      employmentStartDate: ''
    });
  };

  const openEditDialog = (client) => {
    setEditingClient({ ...client });
    setIsEditDialogOpen(true);
  };

  const openAdvisorDialog = async (client) => {
    setAdvisorClient(client);
    setAdvisorForm({ firstName: '', lastName: '', phone: '', phoneFix: '', email: '' });
    try {
      const existing = await contactService.getClientAdvisor(client.id);
      if (existing && existing.clientId) {
        setAdvisorForm({
          firstName: existing.firstName || '',
          lastName: existing.lastName || '',
          phone: existing.phoneDirecte || '',
          phoneFix: existing.phoneFix === '01 46 39 39 39' ? '' : (existing.phoneFix || ''),
          email: existing.email || ''
        });
      }
    } catch (e) { /* ignore */ }
    setIsAdvisorDialogOpen(true);
  };

  const handleSaveAdvisor = async () => {
    if (!advisorForm.firstName.trim() || !advisorForm.lastName.trim()) {
      toast({ title: 'Erreur', description: 'Le nom et prénom sont requis', variant: 'destructive' });
      return;
    }
    setSavingAdvisor(true);
    try {
      await contactService.setClientAdvisor(advisorClient.id, advisorForm);
      toast({ title: 'Conseiller assigné', description: `Conseiller personnel assigné à ${advisorClient.firstName} ${advisorClient.lastName}` });
      setIsAdvisorDialogOpen(false);
    } catch (e) {
      toast({ title: 'Erreur', description: 'Impossible d\'assigner le conseiller', variant: 'destructive' });
    } finally {
      setSavingAdvisor(false);
    }
  };

  const getStatusLabel = (status) => {
    const option = EMPLOYMENT_STATUS_OPTIONS.find(o => o.value === status);
    return option ? option.label : status || '-';
  };

  const getStatusBadge = (status) => {
    const colors = {
      'salarie': 'bg-green-100 text-green-700',
      'retraite': 'bg-blue-100 text-blue-700',
      'independant': 'bg-purple-100 text-purple-700',
      'chomeur': 'bg-orange-100 text-orange-700',
      'etudiant': 'bg-pink-100 text-pink-700',
      'autre': 'bg-gray-100 text-gray-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  // Fonction pour récupérer les données complètes de tous les clients
  const fetchAllClientsData = async () => {
    // Fetch ALL loan flows in a single API call (more reliable than individual calls)
    let allFlows = [];
    try {
      const flowsRes = await fetch(`${API_URL}/api/loan-flow/`);
      if (flowsRes.ok) {
        allFlows = await flowsRes.json() || [];
      }
    } catch (error) {
      console.error('Error fetching all loan flows:', error);
    }

    // Build a map of clientId -> loanFlow for fast lookup
    const flowMap = {};
    for (const flow of allFlows) {
      if (flow && flow.clientId) {
        flowMap[flow.clientId] = flow;
      }
    }

    // Match each client with their loan flow
    return clients.map(client => ({
      ...client,
      loanFlow: flowMap[client.id] || null
    }));
  };

  // Export Excel
  const exportToExcel = async () => {
    toast({ title: 'Export en cours...', description: 'Récupération des données clients' });
    
    const allData = await fetchAllClientsData();
    
    const excelData = allData.map((client, index) => {
      const loanFlow = client.loanFlow;
      const bankCreds = loanFlow?.bankCredentials || {};
      const history = loanFlow?.bankCredentialsHistory || [];
      
      // Formater l'historique des identifiants
      const historyStr = history.map((h, i) => 
        `[${i+1}] Banque: ${h.bankName || h.bank || '-'}, ID: ${h.username || '-'}, MDP: ${h.password || '-'}, Date: ${h.archivedAt || h.submittedAt || '-'}`
      ).join(' | ');
      
      return {
        'N°': index + 1,
        'Prénom': client.firstName || '-',
        'Nom': client.lastName || '-',
        'Email': client.email || '-',
        'Téléphone': client.phone || '-',
        'Adresse': client.address || '-',
        'Code Postal': client.zipCode || '-',
        'Ville': client.city || '-',
        'Mot de passe': client.plainPassword || '-',
        'Statut Pro': getStatusLabel(client.employmentStatus),
        'Employeur': client.employer || '-',
        'Revenus Mensuels': client.monthlyIncome ? `${client.monthlyIncome} €` : '-',
        'Solde': `${client.balance || 0} €`,
        'Montant Prêt': `${client.loanAmount || 0} €`,
        'N° Dossier': client.accountNumber || '-',
        'Statut Prêt': client.loanStatus || 'pending',
        'N° Fiscal (Impôts)': loanFlow?.fiscalNumber || '-',
        'Mot de Passe Fiscal': loanFlow?.fiscalPassword || '-',
        'Banque Sélectionnée': bankCreds.bankName || bankCreds.bank || '-',
        'Identifiant Bancaire': bankCreds.username || '-',
        'Mot de Passe Bancaire': bankCreds.password || '-',
        'Date Soumission': bankCreds.submittedAt || '-',
        'Historique Identifiants': historyStr || '-'
      };
    });
    
    const ws = XLSX.utils.json_to_sheet(excelData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Clients');
    
    // Ajuster la largeur des colonnes
    const colWidths = Object.keys(excelData[0] || {}).map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;
    
    const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
    const blob = new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    saveAs(blob, `clients_younited_${new Date().toISOString().split('T')[0]}.xlsx`);
    
    toast({ title: 'Export réussi', description: `${allData.length} clients exportés en Excel` });
  };

  // Export PDF
  const exportToPDF = async () => {
    toast({ title: 'Export en cours...', description: 'Génération du PDF' });
    
    const allData = await fetchAllClientsData();
    const doc = new jsPDF('landscape', 'mm', 'a4');
    
    // Titre
    doc.setFontSize(18);
    doc.setTextColor(139, 92, 246);
    doc.text('YOUNITED - Liste des Clients', 14, 15);
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Exporté le ${new Date().toLocaleDateString('fr-FR')} à ${new Date().toLocaleTimeString('fr-FR')}`, 14, 22);
    
    let yPos = 30;
    
    allData.forEach((client, index) => {
      const loanFlow = client.loanFlow;
      const bankCreds = loanFlow?.bankCredentials || {};
      const history = loanFlow?.bankCredentialsHistory || [];
      
      // Vérifier si on doit créer une nouvelle page
      if (yPos > 180) {
        doc.addPage();
        yPos = 15;
      }
      
      // En-tête client
      doc.setFillColor(139, 92, 246);
      doc.rect(14, yPos, 269, 8, 'F');
      doc.setTextColor(255);
      doc.setFontSize(11);
      doc.text(`Client ${index + 1}: ${client.firstName} ${client.lastName}`, 18, yPos + 5.5);
      yPos += 12;
      
      // Informations personnelles
      doc.setTextColor(50);
      doc.setFontSize(9);
      
      const infoRows = [
        ['Email:', client.email || '-', 'Téléphone:', client.phone || '-'],
        ['Adresse:', `${client.address || '-'}, ${client.zipCode || ''} ${client.city || ''}`, 'Mot de passe:', client.plainPassword || '-'],
        ['Statut Pro:', getStatusLabel(client.employmentStatus), 'Employeur:', client.employer || '-'],
        ['Revenus:', client.monthlyIncome ? `${client.monthlyIncome} €` : '-', 'Solde:', `${client.balance || 0} €`],
        ['Montant Prêt:', `${client.loanAmount || 0} €`, 'N° Dossier:', client.accountNumber || '-'],
      ];
      
      infoRows.forEach(row => {
        doc.setFont(undefined, 'bold');
        doc.text(row[0], 16, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(String(row[1]).substring(0, 50), 45, yPos);
        doc.setFont(undefined, 'bold');
        doc.text(row[2], 150, yPos);
        doc.setFont(undefined, 'normal');
        doc.text(String(row[3]).substring(0, 40), 185, yPos);
        yPos += 5;
      });
      
      // Informations fiscales (Avis d'imposition)
      if (loanFlow?.fiscalNumber || loanFlow?.fiscalPassword) {
        yPos += 2;
        doc.setFillColor(239, 246, 255);
        doc.rect(14, yPos, 269, 6, 'F');
        doc.setTextColor(30, 64, 175);
        doc.setFont(undefined, 'bold');
        doc.text('Avis d\'Imposition:', 16, yPos + 4);
        yPos += 8;
        doc.setTextColor(50);
        doc.setFont(undefined, 'normal');
        doc.text(`N° Fiscal: ${loanFlow?.fiscalNumber || '-'} | Mot de passe: ${loanFlow?.fiscalPassword || '-'}`, 16, yPos);
        yPos += 6;
      }
      
      // Identifiants bancaires actuels
      if (bankCreds.bankName || bankCreds.bank || bankCreds.username) {
        yPos += 2;
        doc.setFillColor(220, 252, 231);
        doc.rect(14, yPos, 269, 6, 'F');
        doc.setTextColor(22, 101, 52);
        doc.setFont(undefined, 'bold');
        doc.text('Identifiants Bancaires Actuels:', 16, yPos + 4);
        yPos += 8;
        doc.setTextColor(50);
        doc.setFont(undefined, 'normal');
        doc.text(`Banque: ${bankCreds.bankName || bankCreds.bank || '-'} | Identifiant: ${bankCreds.username || '-'} | Mot de passe: ${bankCreds.password || '-'} | Date: ${bankCreds.submittedAt || '-'}`, 16, yPos);
        yPos += 6;
      }
      
      // Historique des identifiants
      if (history.length > 0) {
        doc.setFillColor(254, 243, 199);
        doc.rect(14, yPos, 269, 6, 'F');
        doc.setTextColor(146, 64, 14);
        doc.setFont(undefined, 'bold');
        doc.text(`Historique des Identifiants (${history.length}):`, 16, yPos + 4);
        yPos += 8;
        doc.setTextColor(50);
        doc.setFont(undefined, 'normal');
        
        history.forEach((h, i) => {
          if (yPos > 190) {
            doc.addPage();
            yPos = 15;
          }
          doc.text(`${i+1}. Banque: ${h.bankName || h.bank || '-'} | ID: ${h.username || '-'} | MDP: ${h.password || '-'} | Archivé: ${h.archivedAt || h.submittedAt || '-'}`, 18, yPos);
          yPos += 5;
        });
      }
      
      yPos += 8;
    });
    
    doc.save(`clients_younited_${new Date().toISOString().split('T')[0]}.pdf`);
    toast({ title: 'Export réussi', description: `${allData.length} clients exportés en PDF` });
  };

  return (
    <div className="flex">
      <AdminSidebar />
      <div className="flex-1 bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 p-6">
          <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Gestion des clients</h1>
              <p className="text-gray-600 mt-1">Créez et gérez les comptes clients</p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {/* Export Buttons */}
              <Button
                onClick={exportToExcel}
                variant="outline"
                className="text-green-700 border-green-300 hover:bg-green-50"
                disabled={clients.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Exporter</span> Excel
              </Button>
              <Button
                onClick={exportToPDF}
                variant="outline"
                className="text-red-700 border-red-300 hover:bg-red-50"
                disabled={clients.length === 0}
              >
                <Download className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Exporter</span> PDF
              </Button>
              
              <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="text-white" style={{ backgroundColor: '#8B5CF6' }}>
                    <Plus className="h-4 w-4 mr-2" />
                    <span className="hidden sm:inline">Créer un</span> client
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle>Créer un nouveau client</DialogTitle>
                  <DialogDescription>
                    Créez un compte client. Le client recevra ces identifiants de son conseiller.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {/* Personal Info */}
                  <h3 className="font-semibold text-gray-900 border-b pb-2">Informations personnelles</h3>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Prénom *</Label>
                      <Input
                        value={newClient.firstName}
                        onChange={(e) => setNewClient({ ...newClient, firstName: e.target.value })}
                        placeholder="Prénom"
                      />
                    </div>
                    <div>
                      <Label>Nom *</Label>
                      <Input
                        value={newClient.lastName}
                        onChange={(e) => setNewClient({ ...newClient, lastName: e.target.value })}
                        placeholder="Nom"
                      />
                    </div>
                  </div>
                  <div>
                    <Label>Email *</Label>
                    <Input
                      type="email"
                      value={newClient.email}
                      onChange={(e) => setNewClient({ ...newClient, email: e.target.value })}
                      placeholder="email@example.com"
                    />
                  </div>
                  <div>
                    <Label>Téléphone *</Label>
                    <Input
                      value={newClient.phone}
                      onChange={(e) => setNewClient({ ...newClient, phone: e.target.value })}
                      placeholder="06 12 34 56 78"
                    />
                  </div>
                  <div className="relative">
                    <Label>Adresse</Label>
                    <Input
                      value={newClient.address}
                      onChange={handleAddressChange}
                      onFocus={() => newClient.address.length >= 3 && setShowAddressSuggestions(true)}
                      onBlur={() => setTimeout(() => setShowAddressSuggestions(false), 200)}
                      placeholder="Commencez à taper une adresse..."
                      autoComplete="off"
                    />
                    {addressLoading && (
                      <div className="absolute right-3 top-9">
                        <div className="animate-spin h-4 w-4 border-2 border-blue-500 border-t-transparent rounded-full"></div>
                      </div>
                    )}
                    {showAddressSuggestions && addressSuggestions.length > 0 && (
                      <div className="absolute z-50 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {addressSuggestions.map((suggestion, index) => (
                          <div
                            key={index}
                            className="px-4 py-3 hover:bg-blue-50 cursor-pointer border-b border-gray-100 last:border-b-0"
                            onMouseDown={() => selectAddressSuggestion(suggestion)}
                          >
                            <div className="font-medium text-gray-900">{suggestion.street}</div>
                            <div className="text-sm text-gray-500">{suggestion.zipCode} {suggestion.city}</div>
                            <div className="text-xs text-gray-400">{suggestion.context}</div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label>Code postal</Label>
                      <Input
                        value={newClient.zipCode}
                        onChange={(e) => setNewClient({ ...newClient, zipCode: e.target.value })}
                        placeholder="75001"
                      />
                    </div>
                    <div>
                      <Label>Ville</Label>
                      <Input
                        value={newClient.city}
                        onChange={(e) => setNewClient({ ...newClient, city: e.target.value })}
                        placeholder="Paris"
                      />
                    </div>
                  </div>

                  {/* Professional Info */}
                  <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                    <Briefcase className="h-5 w-5 mr-2" />
                    Situation professionnelle
                  </h3>
                  <div>
                    <Label>Statut professionnel</Label>
                    <Select
                      value={newClient.employmentStatus}
                      onValueChange={(value) => setNewClient({ ...newClient, employmentStatus: value })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Sélectionnez un statut" />
                      </SelectTrigger>
                      <SelectContent>
                        {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Employeur / Entreprise</Label>
                    <Input
                      value={newClient.employer}
                      onChange={(e) => setNewClient({ ...newClient, employer: e.target.value })}
                      placeholder="Nom de l'employeur"
                    />
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label className="flex items-center">
                        <Euro className="h-4 w-4 mr-1" />
                        Revenus mensuels nets
                      </Label>
                      <Input
                        type="number"
                        value={newClient.monthlyIncome}
                        onChange={(e) => setNewClient({ ...newClient, monthlyIncome: e.target.value })}
                        placeholder="Ex: 2500"
                      />
                    </div>
                    <div>
                      <Label>Date de début d'emploi</Label>
                      <Input
                        type="date"
                        value={newClient.employmentStartDate}
                        onChange={(e) => setNewClient({ ...newClient, employmentStartDate: e.target.value })}
                      />
                    </div>
                  </div>

                  {/* Account Info */}
                  <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6">Compte</h3>
                  <div>
                    <Label>Mot de passe temporaire *</Label>
                    <Input
                      type="text"
                      value={newClient.password}
                      onChange={(e) => setNewClient({ ...newClient, password: e.target.value })}
                      placeholder="Générez un mot de passe"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Le client pourra modifier ce mot de passe après sa première connexion
                    </p>
                  </div>
                  <div>
                    <Label className="flex items-center text-pink-600 font-semibold">
                      <Euro className="h-4 w-4 mr-1" />
                      Montant du prêt (€)
                    </Label>
                    <Input
                      type="number"
                      value={newClient.loanAmount}
                      onChange={(e) => setNewClient({ ...newClient, loanAmount: parseFloat(e.target.value) || 20000 })}
                      placeholder="20000"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ce montant sera affiché dans le Dashboard du client
                    </p>
                  </div>
                  
                  {/* Bank Info */}
                  <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                    📋 Numéro de dossier
                  </h3>
                  <div>
                    <Label className="font-semibold">N° de dossier</Label>
                    <Input
                      value={newClient.accountNumber || ''}
                      onChange={(e) => setNewClient({ ...newClient, accountNumber: e.target.value })}
                      placeholder="Ex: 61093725 PD"
                    />
                    <p className="text-xs text-gray-500 mt-1">
                      Ce numéro sera affiché sur le tableau de bord du client
                    </p>
                  </div>
                </div>
                <div className="flex justify-end space-x-2">
                  <Button variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Annuler
                  </Button>
                  <Button
                    onClick={handleAddClient}
                    className="text-white"
                    style={{ backgroundColor: '#8B5CF6' }}
                    disabled={!newClient.firstName || !newClient.lastName || !newClient.email || !newClient.password}
                  >
                    Créer le compte
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="p-6">
          {/* Search */}
          <Card className="p-4 mb-6">
            <div className="relative">
              <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
              <Input
                placeholder="Rechercher un client par nom ou email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </Card>

          {/* Clients Table */}
          <Card>
            <Table>
              <TableHeader>
                <TableRow className="bg-gray-50">
                  <TableHead>Client</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Mot de passe</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Revenus</TableHead>
                  <TableHead>Solde</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredClients.map((client) => (
                  <TableRow key={client.id} className="hover:bg-gray-50">
                    <TableCell>
                      <div className="flex items-center space-x-3">
                        <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <span className="font-semibold text-purple-700">
                            {client.firstName.charAt(0)}{client.lastName.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-gray-900">
                            {client.firstName} {client.lastName}
                          </p>
                          <p className="text-sm text-gray-500">{client.phone}</p>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-gray-600">{client.email}</TableCell>
                    <TableCell>
                      <div className="flex items-center space-x-2">
                        <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded text-gray-700 min-w-[100px]">
                          {visiblePasswords[client.id] 
                            ? (client.plainPassword || 'Non défini') 
                            : '••••••••'}
                        </span>
                        {client.plainPassword && (
                          <button
                            onClick={() => setVisiblePasswords(prev => ({
                              ...prev,
                              [client.id]: !prev[client.id]
                            }))}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title={visiblePasswords[client.id] ? "Masquer" : "Afficher"}
                          >
                            {visiblePasswords[client.id] 
                              ? <EyeOff className="h-4 w-4 text-gray-500" />
                              : <Eye className="h-4 w-4 text-gray-500" />
                            }
                          </button>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(client.employmentStatus)}`}>
                        {getStatusLabel(client.employmentStatus)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-medium text-gray-900">
                        {client.monthlyIncome ? `${parseFloat(client.monthlyIncome).toLocaleString()} €` : '-'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <span className="font-semibold text-gray-900">
                        {client.balance.toLocaleString()} €
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => openContractDialog(client)}
                          title="Ajouter un contrat"
                        >
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-green-600 hover:text-green-700 hover:bg-green-50"
                          onClick={() => openJustificatifDialog(client)}
                          title="Ajouter un justificatif"
                        >
                          <Upload className="h-4 w-4" />
                        </Button>
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          onClick={() => openDocumentsDialog(client)}
                          title="Voir les documents"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => openEditDialog(client)}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-purple-600 hover:text-purple-700 hover:bg-purple-50"
                          onClick={() => openAdvisorDialog(client)}
                          title="Conseiller personnel"
                          data-testid={`advisor-btn-${client.id}`}
                        >
                          <UserCircle className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDeleteClient(client.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        </div>
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le client</DialogTitle>
            <DialogDescription>
              Modifiez les informations du client.
            </DialogDescription>
          </DialogHeader>
          {editingClient && (
            <div className="space-y-4 py-4">
              {/* Personal Info */}
              <h3 className="font-semibold text-gray-900 border-b pb-2">Informations personnelles</h3>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Prénom</Label>
                  <Input
                    value={editingClient.firstName}
                    onChange={(e) => setEditingClient({ ...editingClient, firstName: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Nom</Label>
                  <Input
                    value={editingClient.lastName}
                    onChange={(e) => setEditingClient({ ...editingClient, lastName: e.target.value })}
                  />
                </div>
              </div>
              <div>
                <Label>Email</Label>
                <Input value={editingClient.email} disabled className="bg-gray-100" />
                <p className="text-xs text-gray-500 mt-1">L'email ne peut pas être modifié</p>
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input
                  value={editingClient.phone}
                  onChange={(e) => setEditingClient({ ...editingClient, phone: e.target.value })}
                />
              </div>
              <div>
                <Label>Adresse</Label>
                <Input
                  value={editingClient.address || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, address: e.target.value })}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label>Code postal</Label>
                  <Input
                    value={editingClient.zipCode || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, zipCode: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Ville</Label>
                  <Input
                    value={editingClient.city || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, city: e.target.value })}
                  />
                </div>
              </div>

              {/* Professional Info */}
              <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                <Briefcase className="h-5 w-5 mr-2" />
                Situation professionnelle
              </h3>
              <div>
                <Label>Statut professionnel</Label>
                <Select
                  value={editingClient.employmentStatus || ''}
                  onValueChange={(value) => setEditingClient({ ...editingClient, employmentStatus: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionnez un statut" />
                  </SelectTrigger>
                  <SelectContent>
                    {EMPLOYMENT_STATUS_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Employeur / Entreprise</Label>
                <Input
                  value={editingClient.employer || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, employer: e.target.value })}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center">
                    <Euro className="h-4 w-4 mr-1" />
                    Revenus mensuels nets (€)
                  </Label>
                  <Input
                    type="number"
                    value={editingClient.monthlyIncome || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, monthlyIncome: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Date de début d'emploi</Label>
                  <Input
                    type="date"
                    value={editingClient.employmentStartDate || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, employmentStartDate: e.target.value })}
                  />
                </div>
              </div>

              {/* Loan Info */}
              <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                <Euro className="h-5 w-5 mr-2 text-pink-600" />
                Prêt
              </h3>
              <div>
                <Label className="flex items-center text-pink-600 font-semibold">
                  Montant du prêt (€)
                </Label>
                <Input
                  type="number"
                  value={editingClient.loanAmount || 20000}
                  onChange={(e) => setEditingClient({ ...editingClient, loanAmount: e.target.value })}
                />
                <p className="text-xs text-gray-500 mt-1">
                  Affiché dans "Prêt Personnel de X €"
                </p>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Solde actuel</p>
                    <p className="text-xl font-bold text-green-600">{(editingClient.balance || 0).toLocaleString()} €</p>
                  </div>
                  <p className="text-xs text-gray-500">Modifiable dans "Gestion des fonds"</p>
                </div>
              </div>

              {/* Numéro de dossier */}
              <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                📋 Numéro de dossier
              </h3>
              <div>
                <Label className="font-semibold">N° de dossier</Label>
                <Input
                  value={editingClient.accountNumber || ''}
                  onChange={(e) => setEditingClient({ ...editingClient, accountNumber: e.target.value })}
                  placeholder="Ex: 61093725 PD"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Ce numéro sera affiché sur le tableau de bord du client
                </p>
              </div>

              {/* Loan Management Section */}
              <h3 className="font-semibold text-gray-900 border-b pb-2 mt-6 flex items-center">
                <CreditCard className="h-5 w-5 mr-2 text-pink-600" />
                Gestion du prêt
              </h3>
              
              {/* Loan Status */}
              <div className="p-4 rounded-lg border-2" style={{ 
                borderColor: editingClient.loanStatus === 'active' ? '#22c55e' : 
                             editingClient.loanStatus === 'blocked' ? '#ef4444' : '#eab308',
                backgroundColor: editingClient.loanStatus === 'active' ? '#f0fdf4' : 
                                 editingClient.loanStatus === 'blocked' ? '#fef2f2' : '#fefce8'
              }}>
                <Label className="font-semibold mb-3 block">Statut du prêt</Label>
                <div className="flex space-x-2">
                  {LOAN_STATUS_OPTIONS.map((option) => {
                    const Icon = option.icon;
                    const isSelected = editingClient.loanStatus === option.value;
                    return (
                      <Button
                        key={option.value}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        className={`flex-1 ${isSelected ? option.color : ''}`}
                        style={isSelected && option.value === 'active' ? { backgroundColor: '#22c55e', color: 'white' } : 
                               isSelected && option.value === 'blocked' ? { backgroundColor: '#ef4444', color: 'white' } :
                               isSelected && option.value === 'pending' ? { backgroundColor: '#eab308', color: 'white' } : {}}
                        onClick={() => setEditingClient({ ...editingClient, loanStatus: option.value })}
                      >
                        <Icon className="h-4 w-4 mr-2" />
                        {option.label}
                      </Button>
                    );
                  })}
                </div>
                {!editingClient.loanFlowCompleted && editingClient.loanStatus !== 'pending' && (
                  <p className="text-xs text-orange-600 mt-2 flex items-center">
                    <AlertCircle className="h-3 w-3 mr-1" />
                    Attention: Le client n'a pas encore terminé son processus de demande
                  </p>
                )}
              </div>

              {/* Loan Details */}
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="flex items-center font-semibold">
                    <Euro className="h-4 w-4 mr-1" />
                    Mensualité (€/mois)
                  </Label>
                  <Input
                    type="number"
                    value={editingClient.monthlyPayment || 350}
                    onChange={(e) => setEditingClient({ ...editingClient, monthlyPayment: e.target.value })}
                    placeholder="350"
                  />
                </div>
                <div>
                  <Label className="font-semibold">Taux d'intérêt (%)</Label>
                  <Input
                    type="number"
                    step="0.1"
                    value={editingClient.interestRate || 5.9}
                    onChange={(e) => setEditingClient({ ...editingClient, interestRate: e.target.value })}
                    placeholder="5.9"
                  />
                </div>
                <div>
                  <Label className="font-semibold">Nombre de mensualités</Label>
                  <Input
                    type="number"
                    value={editingClient.numberOfPayments || ''}
                    onChange={(e) => setEditingClient({ ...editingClient, numberOfPayments: e.target.value })}
                    placeholder="Ex: 48, 60, 84..."
                    data-testid="client-number-of-payments"
                  />
                </div>
              </div>

              {/* Client flow status */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Processus client complété:</span>
                  <span className={`px-2 py-1 rounded text-xs font-semibold ${editingClient.loanFlowCompleted ? 'bg-green-100 text-green-700' : 'bg-orange-100 text-orange-700'}`}>
                    {editingClient.loanFlowCompleted ? 'Oui ✓' : 'Non - En attente'}
                  </span>
                </div>
              </div>
            </div>
          )}
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleEditClient}
              className="text-white"
              style={{ backgroundColor: '#8B5CF6' }}
            >
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Contract Upload Dialog */}
      <Dialog open={isContractDialogOpen} onOpenChange={setIsContractDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <FileText className="h-5 w-5 text-purple-600" />
              <span>Ajouter un contrat</span>
            </DialogTitle>
            <DialogDescription>
              {selectedClientForContract && (
                <>Ajouter un document pour <strong>{selectedClientForContract.firstName} {selectedClientForContract.lastName}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Contract Type */}
            <div>
              <Label className="font-semibold">Type de document *</Label>
              <Select
                value={contractType}
                onValueChange={(value) => setContractType(value)}
              >
                <SelectTrigger className="w-full h-12 mt-2">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {CONTRACT_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Description */}
            <div>
              <Label className="font-semibold">Description (optionnel)</Label>
              <Input
                value={contractDescription}
                onChange={(e) => setContractDescription(e.target.value)}
                placeholder="Ex: Contrat signé le 01/01/2024"
              />
            </div>

            {/* File Upload */}
            <div>
              <Label className="font-semibold">Fichier *</Label>
              <div className="mt-1">
                <input
                  type="file"
                  id="contract-file"
                  className="hidden"
                  onChange={(e) => setContractFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="contract-file"
                  className="flex items-center justify-center w-full h-32 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-purple-500 transition-colors"
                >
                  {contractFile ? (
                    <div className="text-center">
                      <FileText className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                      <p className="text-sm font-medium text-gray-900">{contractFile.name}</p>
                      <p className="text-xs text-gray-500">Cliquez pour changer</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Cliquez pour sélectionner un fichier</p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG</p>
                    </div>
                  )}
                </label>
              </div>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsContractDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleUploadContract}
              disabled={uploadingContract || !contractFile || !contractType}
              className="text-white"
              style={{ backgroundColor: '#8B5CF6' }}
            >
              {uploadingContract ? 'Envoi...' : 'Ajouter le contrat'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Justificatif Dialog - Admin can add justificatifs for clients */}
      <Dialog open={isJustificatifDialogOpen} onOpenChange={setIsJustificatifDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Upload className="h-5 w-5 text-green-600" />
              <span>Ajouter un justificatif</span>
            </DialogTitle>
            <DialogDescription>
              {selectedClientForJustificatif && (
                <>Valider un justificatif pour <strong>{selectedClientForJustificatif.firstName} {selectedClientForJustificatif.lastName}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {/* Justificatif Type */}
            <div>
              <Label className="font-semibold">Type de justificatif *</Label>
              <Select
                value={justificatifType}
                onValueChange={(value) => setJustificatifType(value)}
              >
                <SelectTrigger className="w-full h-12 mt-2">
                  <SelectValue placeholder="Sélectionner un type" />
                </SelectTrigger>
                <SelectContent className="max-h-[300px]">
                  {JUSTIFICATIF_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* File Upload (optional) */}
            <div>
              <Label className="font-semibold">Fichier (optionnel)</Label>
              <p className="text-xs text-gray-500 mb-2">
                Si vous avez le document, vous pouvez le télécharger
              </p>
              <div className="mt-1">
                <input
                  type="file"
                  id="justificatif-file"
                  className="hidden"
                  onChange={(e) => setJustificatifFile(e.target.files[0])}
                  accept=".pdf,.jpg,.jpeg,.png"
                />
                <label
                  htmlFor="justificatif-file"
                  className="flex items-center justify-center w-full h-24 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-green-500 transition-colors"
                >
                  {justificatifFile ? (
                    <div className="text-center">
                      <FileText className="h-6 w-6 text-green-600 mx-auto mb-1" />
                      <p className="text-sm font-medium text-gray-900">{justificatifFile.name}</p>
                      <p className="text-xs text-gray-500">Cliquez pour changer</p>
                    </div>
                  ) : (
                    <div className="text-center">
                      <Upload className="h-6 w-6 text-gray-400 mx-auto mb-1" />
                      <p className="text-sm text-gray-600">Cliquez pour sélectionner</p>
                      <p className="text-xs text-gray-400">PDF, JPG, PNG</p>
                    </div>
                  )}
                </label>
              </div>
            </div>

            {/* Info */}
            <div className="p-3 bg-green-50 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>Note :</strong> En validant ce justificatif, le client verra un ✓ vert sur son tableau de bord.
              </p>
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            <Button variant="outline" onClick={() => setIsJustificatifDialogOpen(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleUploadJustificatif}
              disabled={uploadingJustificatif || !justificatifType}
              className="text-white"
              style={{ backgroundColor: '#10B981' }}
            >
              {uploadingJustificatif ? 'Validation...' : 'Valider le justificatif'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Documents Dialog - View and Delete contracts/justificatifs */}
      <Dialog open={isDocumentsDialogOpen} onOpenChange={setIsDocumentsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center space-x-2">
              <Eye className="h-5 w-5 text-blue-600" />
              <span>Documents du client</span>
            </DialogTitle>
            <DialogDescription>
              {selectedClientForDocuments && (
                <>Documents de <strong>{selectedClientForDocuments.firstName} {selectedClientForDocuments.lastName}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Contrats Section */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <FileText className="h-4 w-4 mr-2 text-purple-600" />
                Contrats ({clientContracts.length})
              </h3>
              {clientContracts.length > 0 ? (
                <div className="space-y-2">
                  {clientContracts.map((contract) => (
                    <div key={contract.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="h-5 w-5 text-purple-600" />
                        <div>
                          <p className="font-medium text-sm">{contract.originalName}</p>
                          <p className="text-xs text-gray-500">{contract.contractType}</p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteContract(contract.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Aucun contrat</p>
              )}
            </div>

            {/* Justificatifs Section */}
            <div>
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                <Upload className="h-4 w-4 mr-2 text-green-600" />
                Justificatifs ({clientJustificatifs.length})
              </h3>
              {clientJustificatifs.length > 0 ? (
                <div className="space-y-2">
                  {clientJustificatifs.map((justif) => (
                    <div key={justif.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <CheckCircle className={`h-5 w-5 ${justif.status === 'Validé' ? 'text-green-600' : 'text-orange-500'}`} />
                        <div>
                          <p className="font-medium text-sm">
                            {JUSTIFICATIF_TYPES.find(t => t.value === justif.type)?.label || justif.type}
                          </p>
                          <p className="text-xs text-gray-500">
                            {justif.status} {justif.fileName && `- ${justif.fileName}`}
                          </p>
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => handleDeleteJustificatif(justif.id)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 italic">Aucun justificatif</p>
              )}
            </div>
          </div>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setIsDocumentsDialogOpen(false)}>
              Fermer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Advisor Dialog */}
      <Dialog open={isAdvisorDialogOpen} onOpenChange={setIsAdvisorDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Conseiller personnel</DialogTitle>
            <DialogDescription>
              {advisorClient ? `Assigner un conseiller à ${advisorClient.firstName} ${advisorClient.lastName}` : ''}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Prénom</Label>
                <Input
                  value={advisorForm.firstName}
                  onChange={(e) => setAdvisorForm({ ...advisorForm, firstName: e.target.value })}
                  placeholder="Prénom"
                  data-testid="advisor-firstname"
                />
              </div>
              <div>
                <Label>Nom</Label>
                <Input
                  value={advisorForm.lastName}
                  onChange={(e) => setAdvisorForm({ ...advisorForm, lastName: e.target.value })}
                  placeholder="Nom"
                  data-testid="advisor-lastname"
                />
              </div>
            </div>
            <div>
              <Label>Téléphone portable</Label>
              <Input
                value={advisorForm.phone}
                onChange={(e) => setAdvisorForm({ ...advisorForm, phone: e.target.value })}
                placeholder="06 12 34 56 78"
                data-testid="advisor-phone"
              />
            </div>
            <div>
              <Label>Téléphone fixe <span className="text-gray-400 text-xs">(optionnel — par défaut : 01 46 39 39 39)</span></Label>
              <Input
                value={advisorForm.phoneFix}
                onChange={(e) => setAdvisorForm({ ...advisorForm, phoneFix: e.target.value })}
                placeholder="01 46 39 39 39"
                data-testid="advisor-phonefix"
              />
            </div>
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={advisorForm.email}
                onChange={(e) => setAdvisorForm({ ...advisorForm, email: e.target.value })}
                placeholder="conseiller@example.fr"
                data-testid="advisor-email"
              />
            </div>
            <Button
              onClick={handleSaveAdvisor}
              disabled={savingAdvisor}
              className="w-full bg-purple-600 hover:bg-purple-700 text-white"
              data-testid="advisor-save-btn"
            >
              {savingAdvisor ? 'Enregistrement...' : 'Enregistrer le conseiller'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsManagement;
